/**
 * LAPORAN SHIFT DUAL BANK - PROFESSIONAL VERSION
 *
 * Fitur:
 * - 2 Tab: BSI & Bank Mandiri
 * - Setiap provider bisa memiliki transaksi TUNAI dan NON-TUNAI
 * - Auto-calculate saldo akhir per bank
 * - Real-time validation dan calculation
 * - Professional UI dengan checkbox toggle
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Save, RefreshCw, CheckCircle, AlertCircle, DollarSign, Wallet, Info, Calculator, Upload, X } from 'lucide-react';
import SimpleCalculator from './components/SimpleCalculator';

// Helper function untuk format rupiah
const formatRupiah = (num) => {
  if (!num && num !== 0) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);
};

const LaporanShiftDualBankV2 = ({ user, api }) => {
  // State management
  const [providers, setProviders] = useState([]);
  const [shiftSettings, setShiftSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSaldo, setLoadingSaldo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdLaporan, setCreatedLaporan] = useState(null);

  // Active tab: 'BSI' or 'MANDIRI'
  const [activeTab, setActiveTab] = useState('BSI');
  
  // Calculator and upload states
  const [showCalculator, setShowCalculator] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  
  // Transaction history untuk current session
  const [transactionHistory, setTransactionHistory] = useState([]);

  // Saldo awal dari shift sebelumnya
  const [saldoAwal, setSaldoAwal] = useState({
    bsi: 0,
    mandiri: 0,
    loaded: false,
    lastDate: null
  });

  // Permission untuk edit saldo manual - Removed: Gunakan menu Inject Saldo di Manager role

  // Form data
  const [formData, setFormData] = useState({
    shift_period: 'PAGI',
    catatan: ''
  });

  // Uang kecil/kembalian (gabungan untuk kedua bank)
  const [uangKecil, setUangKecil] = useState(0);

  // Transaksi structure:
  // { BSI: { provider_id: { tunai: {...}, non_tunai: {...} } }, MANDIRI: {...} }
  const [bankTransaksi, setBankTransaksi] = useState({
    BSI: {},
    MANDIRI: {}
  });

  // Provider deposits balance state
  const [deposits, setDeposits] = useState([]);

  // Memoized providers (semua provider ditampilkan, tidak ada tab khusus)
  const standardProviders = useMemo(() => {
    return providers;
  }, [providers]);

  // Shift timing inline editor state (for Owners/Managers)
  const activeShiftObj = useMemo(() => {
    return shiftSettings.find(s => s.shift_name === formData.shift_period);
  }, [shiftSettings, formData.shift_period]);

  const [editShiftTimes, setEditShiftTimes] = useState({ jam_mulai: '', jam_selesai: '' });

  useEffect(() => {
    if (activeShiftObj) {
      setEditShiftTimes({
        jam_mulai: activeShiftObj.jam_mulai,
        jam_selesai: activeShiftObj.jam_selesai
      });
    }
  }, [activeShiftObj]);

  // Load providers and shift settings
  useEffect(() => {
    loadProviders();
    loadShiftSettings();
    loadSaldoAwal();
    loadDeposits();
  }, [user?.loket_id]);

  const loadDeposits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/deposits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeposits(data);
      }
    } catch (err) {
      console.error('Gagal memuat deposit balance:', err);
    }
  };

  const handleSaveShiftTimes = async () => {
    if (!activeShiftObj) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/settings/shifts/${activeShiftObj.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          jam_mulai: editShiftTimes.jam_mulai,
          jam_selesai: editShiftTimes.jam_selesai
        })
      });

      if (response.ok) {
        setSuccess(`Jam shift ${formData.shift_period} berhasil diperbarui!`);
        await loadShiftSettings();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Gagal menyimpan waktu shift');
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      setError('Kesalahan koneksi: ' + err.message);
      setTimeout(() => setError(''), 4000);
    }
  };

  const loadProviders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const provs = await response.json();

      // Filter hanya provider yang AKTIF
      const activeProvs = provs.filter(p => p.is_active !== false);
      setProviders(activeProvs);

      // Initialize transaksi structure for both banks
      const initTransaksi = {};
      activeProvs.forEach(p => {
        initTransaksi[p.id] = {
          provider_id: p.id,
          provider_name: p.nama_provider,
          kategori: p.kategori,
          biaya_admin: p.biaya_admin || 0,  // PENTING: Simpan biaya admin!
          // TUNAI transactions
          tunai: {
            jumlah: 0,
            tagihan: 0,
            admin_fee: 0,
            checked: false // Checkbox to enable tunai input
          },
          // NON-TUNAI transactions
          non_tunai: {
            jumlah: 0,
            tagihan: 0,
            admin_fee: 0,
            checked: false // Checkbox to enable non-tunai input
          }
        };
      });

      setBankTransaksi({
        BSI: { ...initTransaksi },
        MANDIRI: { ...initTransaksi }
      });
    } catch (err) {
      setError('Gagal memuat provider: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadShiftSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/settings/shifts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const shifts = await response.json();
      setShiftSettings(shifts.filter(s => s.is_active));
    } catch (err) {
      console.error('Gagal memuat shift settings:', err);
    }
  };

  const loadSaldoAwal = async () => {
    if (!user?.loket_id) return;

    setLoadingSaldo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/laporan-shift/saldo-awal?loket_id=${user.loket_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSaldoAwal({
        bsi: data.saldo_awal_bsi || 0,
        mandiri: data.saldo_awal_mandiri || 0,
        loaded: true,
        lastDate: data.last_date
      });
    } catch (err) {
      console.error('Gagal memuat saldo awal:', err);
      setSaldoAwal(prev => ({ ...prev, loaded: true }));
    } finally {
      setLoadingSaldo(false);
    }
  };

  // Check permission untuk edit saldo manual - Removed: Gunakan menu Inject Saldo

  // Update transaksi (tunai atau non_tunai) - optimized with useCallback
  const updateTransaksi = useCallback((bankId, providerId, tipe, field, value) => {
    setBankTransaksi(prev => {
      // Create new state efficiently
      const bankData = prev[bankId] || {};
      const providerData = bankData[providerId] || createEmptyProvider();

      // Parse numeric value
      const numValue = field === 'jumlah' ? parseInt(value) || 0 : parseFloat(value) || 0;

      // Calculate admin fee when jumlah is updated
      const biayaAdmin = providerData.biaya_admin || 0;
      const newTipeData = {
        ...providerData[tipe],
        [field]: numValue
      };

      // Auto-calculate admin_fee when jumlah changes
      if (field === 'jumlah') {
        newTipeData.admin_fee = numValue * biayaAdmin;
      }

      return {
        ...prev,
        [bankId]: {
          ...bankData,
          [providerId]: {
            ...providerData,
            [tipe]: newTipeData
          }
        }
      };
    });
  }, []);

  // Toggle checkbox untuk tunai/non-tunai - optimized with useCallback
  const toggleTransactionType = useCallback((bankId, providerId, tipe) => {
    setBankTransaksi(prev => ({
      ...prev,
      [bankId]: {
        ...prev[bankId],
        [providerId]: {
          ...prev[bankId][providerId],
          [tipe]: {
            ...prev[bankId][providerId][tipe],
            checked: !prev[bankId][providerId][tipe].checked,
            // Reset values jika unchecked
            jumlah: !prev[bankId][providerId][tipe].checked ? 0 : prev[bankId][providerId][tipe].jumlah,
            tagihan: !prev[bankId][providerId][tipe].checked ? 0 : prev[bankId][providerId][tipe].tagihan,
            admin_fee: !prev[bankId][providerId][tipe].checked ? 0 : prev[bankId][providerId][tipe].admin_fee
          }
        }
      }
    }));
  }, []);

  // Calculate totals per provider - memoized
  const calculateProviderTotal = useCallback((providerData) => {
    let total_jumlah = 0;
    let total_tagihan = 0;
    let total_admin = 0;

    // TUNAI
    if (providerData.tunai.checked) {
      total_jumlah += providerData.tunai.jumlah || 0;
      total_tagihan += providerData.tunai.tagihan || 0;
      total_admin += providerData.tunai.admin_fee || 0;
    }

    // NON-TUNAI
    if (providerData.non_tunai.checked) {
      total_jumlah += providerData.non_tunai.jumlah || 0;
      total_tagihan += providerData.non_tunai.tagihan || 0;
      total_admin += providerData.non_tunai.admin_fee || 0;
    }

    return { total_jumlah, total_tagihan, total_admin };
  }, []);

  // Calculate bank totals - memoized
  const calculateBankTotals = useCallback((bankId) => {
    const bankData = bankTransaksi[bankId];
    if (!bankData) return { trx: 0, tagihan: 0, admin: 0, tunai_trx: 0, non_tunai_trx: 0, tunai_amount: 0, non_tunai_amount: 0 };

    let trx = 0, tagihan = 0, admin = 0, tunai_trx = 0, non_tunai_trx = 0;
    let tunai_amount = 0, non_tunai_amount = 0;

    Object.values(bankData).forEach(p => {
      const total = calculateProviderTotal(p);
      trx += total.total_jumlah;
      tagihan += total.total_tagihan;
      admin += total.total_admin;

      if (p.tunai.checked) {
        tunai_trx += p.tunai.jumlah || 0;
        tunai_amount += (p.tunai.tagihan || 0) + (p.tunai.admin_fee || 0);
      }
      if (p.non_tunai.checked) {
        non_tunai_trx += p.non_tunai.jumlah || 0;
        non_tunai_amount += (p.non_tunai.tagihan || 0) + (p.non_tunai.admin_fee || 0);
      }
    });

    return { trx, tagihan, admin, tunai_trx, non_tunai_trx, tunai_amount, non_tunai_amount };
  }, [bankTransaksi, calculateProviderTotal]);

  // Calculate bank ending balance
  const calculateBankEndingBalance = (bankId) => {
    const saldoAwalBank = bankId === 'BSI' ? saldoAwal.bsi : saldoAwal.mandiri;
    const totals = calculateBankTotals(bankId);
    const saldoAkhir = saldoAwalBank - totals.tagihan - totals.admin;

    return {
      saldo_awal: saldoAwalBank,
      total_tagihan: totals.tagihan,
      total_admin: totals.admin,
      total_pengurangan: totals.tagihan + totals.admin,
      saldo_akhir: saldoAkhir,
      total_transaksi: totals.trx,
      tunai_trx: totals.tunai_trx,
      non_tunai_trx: totals.non_tunai_trx,
      tunai_amount: totals.tunai_amount,
      non_tunai_amount: totals.non_tunai_amount
    };
  };

  // Memoized bank calculations to prevent re-renders
  const bsiCalculations = useMemo(() => calculateBankEndingBalance('BSI'), [bankTransaksi, saldoAwal.bsi]);
  const mandiriCalculations = useMemo(() => calculateBankEndingBalance('MANDIRI'), [bankTransaksi, saldoAwal.mandiri]);

  // Calculate grand totals
  const calculateGrandTotals = () => {
    const bsi = calculateBankTotals('BSI');
    const mandiri = calculateBankTotals('MANDIRI');

    return {
      total_transaksi: bsi.trx + mandiri.trx,
      total_tagihan: bsi.tagihan + mandiri.tagihan,
      total_admin: bsi.admin + mandiri.admin,
      total_omzet: bsi.tagihan + bsi.admin + mandiri.tagihan + mandiri.admin,
      total_tunai_trx: bsi.tunai_trx + mandiri.tunai_trx,
      total_non_tunai_trx: bsi.non_tunai_trx + mandiri.non_tunai_trx,
      total_tunai_amount: bsi.tunai_amount + mandiri.tunai_amount,
      total_non_tunai_amount: bsi.non_tunai_amount + mandiri.non_tunai_amount,
      uang_kecil: uangKecil
    };
  };

  // Submit form

  // Calculator result handler
  const handleCalculatorResult = (result) => {
    // Result will be used for quick calculations
    console.log('Calculator result:', result);
  };

  // Upload proof handler
  const handleUploadProof = async () => {
    if (!uploadFile) {
      setError('Pilih file bukti transaksi');
      return;
    }
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      // Get token from localStorage
      const token = localStorage.getItem('token');

      // Upload to backend - FIXED: Use correct endpoint format
      const response = await fetch(`${api}/api/laporan-shift/${createdLaporan?.id}/upload-bukti`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      setSuccess('Bukti transaksi berhasil diupload!');
      setShowUploadModal(false);
      setUploadFile(null);
    } catch (err) {
      setError('Gagal upload bukti: ' + err.message);
    } finally {
      setUploadingProof(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.loket_id) {
      setError('Anda tidak terdaftar di loket manapun');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Prepare transactions for BRI
      const transaksiBRI = [];
      Object.values(bankTransaksi.BSI).forEach(p => {
        const total = calculateProviderTotal(p);
        if (total.total_jumlah > 0) {
          // Add TUNAI transactions
          if (p.tunai.checked && p.tunai.jumlah > 0) {
            transaksiBRI.push({
              provider_id: p.provider_id,
              jumlah_transaksi: p.tunai.jumlah,
              total_tagihan: p.tunai.tagihan,
              total_admin_fee: p.tunai.jumlah * p.biaya_admin,  // OTOMATIS: jumlah × biaya_admin
              tipe_transaksi: 'TUNAI'
            });
          }
          // Add NON-TUNAI transactions
          if (p.non_tunai.checked && p.non_tunai.jumlah > 0) {
            transaksiBRI.push({
              provider_id: p.provider_id,
              jumlah_transaksi: p.non_tunai.jumlah,
              total_tagihan: p.non_tunai.tagihan,
              total_admin_fee: p.non_tunai.jumlah * p.biaya_admin,  // OTOMATIS: jumlah × biaya_admin
              tipe_transaksi: 'NON_TUNAI'
            });
          }
        }
      });

      // Prepare transactions for Mandiri
      const transaksiMandiri = [];
      Object.values(bankTransaksi.MANDIRI).forEach(p => {
        const total = calculateProviderTotal(p);
        if (total.total_jumlah > 0) {
          if (p.tunai.checked && p.tunai.jumlah > 0) {
            transaksiMandiri.push({
              provider_id: p.provider_id,
              jumlah_transaksi: p.tunai.jumlah,
              total_tagihan: p.tunai.tagihan,
              total_admin_fee: p.tunai.jumlah * p.biaya_admin,  // OTOMATIS: jumlah × biaya_admin
              tipe_transaksi: 'TUNAI'
            });
          }
          if (p.non_tunai.checked && p.non_tunai.jumlah > 0) {
            transaksiMandiri.push({
              provider_id: p.provider_id,
              jumlah_transaksi: p.non_tunai.jumlah,
              total_tagihan: p.non_tunai.tagihan,
              total_admin_fee: p.non_tunai.jumlah * p.biaya_admin,  // OTOMATIS: jumlah × biaya_admin
              tipe_transaksi: 'NON_TUNAI'
            });
          }
        }
      });

      if (transaksiBRI.length === 0 && transaksiMandiri.length === 0) {
        setError('Mohon isi minimal satu transaksi (TUNAI atau NON_TUNAI)');
        return;
      }

      const payload = {
        loket_id: user.loket_id,
        shift_period: formData.shift_period,
        transaksi_bsi: transaksiBRI,
        transaksi_mandiri: transaksiMandiri,
        saldo_awal_bsi: saldoAwal.bsi,
        saldo_awal_mandiri: saldoAwal.mandiri,
        uang_kecil: uangKecil,
        catatan: formData.catatan
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/laporan-shift`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Cek apakah response berhasil
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Gagal membuat laporan');
      }

      const result = await response.json();
      setCreatedLaporan(result);
      setSuccess(`Laporan shift ${result.shift_period || formData.shift_period} berhasil dibuat! Saldo awal untuk shift berikutnya akan diperbarui otomatis.`);

      // Reset form tapi tetap di halaman yang sama
      // PENTING: Load ulang saldo awal untuk shift berikutnya
      setTimeout(async () => {
        setBankTransaksi({});
        setFormData({
          shift_period: 'PAGI',
          catatan: ''
        });
        setUangKecil(0);
        // Reload saldo awal agar shift berikutnya menggunakan saldo akhir dari shift ini
        await loadSaldoAwal();
        setSuccess('Saldo awal telah diperbarui! Silakan buat laporan untuk shift berikutnya.');
      }, 3000);

    } catch (err) {
      setError('Gagal membuat laporan: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handler untuk edit saldo manual - Removed: Gunakan menu Inject Saldo

  // Provider Transaction Input Component - memoized to prevent unnecessary re-renders
  const ProviderTransactionInput = React.memo(({ provider, bankId }) => {
    const pData = bankTransaksi[bankId]?.[provider.id];
    const providerDeposit = deposits.find(d => d.provider_id === provider.id);

    if (!pData) return null;

    const hasTunai = provider.allow_tunai !== false;
    const hasNonTunai = provider.allow_non_tunai !== false;

    return (
      <div key={provider.id} className="bg-white rounded-lg shadow p-4 mb-3 border border-gray-100 hover:border-gray-200 transition-all">
        {/* Provider Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-50">
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 text-base">{provider.nama_provider}</h4>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{provider.kategori}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {provider.biaya_admin > 0 && (
                <span className="inline-flex items-center text-xs text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded font-medium">
                  💰 Admin: Rp {provider.biaya_admin.toLocaleString('id-ID')}
                </span>
              )}
              {providerDeposit !== undefined && (
                <span className="inline-flex items-center text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded font-semibold">
                  🏦 Deposit: Rp {providerDeposit.saldo.toLocaleString('id-ID')}
                </span>
              )}
            </div>
          </div>
        </div>

        {!hasTunai && !hasNonTunai && (
          <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs italic">
            ⚠️ Tidak ada tipe transaksi yang diaktifkan untuk provider ini. Hubungi Manager/Owner.
          </div>
        )}

        {/* TUNAI Section */}
        {hasTunai && (
          <div className="mb-3 p-3 bg-green-50/50 rounded-lg border border-green-100">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 font-semibold text-green-800 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={pData.tunai.checked}
                  onChange={() => toggleTransactionType(bankId, provider.id, 'tunai')}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500 border-gray-300"
                />
                <Wallet size={16} className="text-green-600" />
                Transaksi TUNAI
              </label>
              {pData.tunai.checked && (
                <span className="text-[10px] bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-bold uppercase">
                  Aktif
                </span>
              )}
            </div>

            {pData.tunai.checked && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase">Jumlah Trx</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={pData.tunai.jumlah || ''}
                    onBlur={(e) => updateTransaksi(bankId, provider.id, 'tunai', 'jumlah', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm focus:outline-none"
                    placeholder="0"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase">Total Tagihan</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={pData.tunai.tagihan || ''}
                    onBlur={(e) => updateTransaksi(bankId, provider.id, 'tunai', 'tagihan', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm focus:outline-none"
                    placeholder="0"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase">Admin Fee (Otomatis)</label>
                  <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded bg-gray-50 text-gray-700 text-sm">
                    {formatRupiah((pData.tunai.jumlah || 0) * provider.biaya_admin)}
                    <span className="text-[10px] text-gray-400 block mt-0.5">
                      {pData.tunai.jumlah || 0} trx × {formatRupiah(provider.biaya_admin)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* NON-TUNAI Section */}
        {hasNonTunai && (
          <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 font-semibold text-blue-800 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={pData.non_tunai.checked}
                  onChange={() => toggleTransactionType(bankId, provider.id, 'non_tunai')}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                />
                <DollarSign size={16} className="text-blue-600" />
                Transaksi NON-TUNAI
              </label>
              {pData.non_tunai.checked && (
                <span className="text-[10px] bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-bold uppercase">
                  Aktif
                </span>
              )}
            </div>

            {pData.non_tunai.checked && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase">Jumlah Trx</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={pData.non_tunai.jumlah || ''}
                    onBlur={(e) => updateTransaksi(bankId, provider.id, 'non_tunai', 'jumlah', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm focus:outline-none"
                    placeholder="0"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase">Total Tagihan</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={pData.non_tunai.tagihan || ''}
                    onBlur={(e) => updateTransaksi(bankId, provider.id, 'non_tunai', 'tagihan', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm focus:outline-none"
                    placeholder="0"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase">Admin Fee (Otomatis)</label>
                  <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded bg-gray-50 text-gray-700 text-sm">
                    {formatRupiah((pData.non_tunai.jumlah || 0) * provider.biaya_admin)}
                    <span className="text-[10px] text-gray-400 block mt-0.5">
                      {pData.non_tunai.jumlah || 0} trx × {formatRupiah(provider.biaya_admin)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Provider Total */}
        {((hasTunai && pData.tunai.checked) || (hasNonTunai && pData.non_tunai.checked)) && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center text-xs">
            <span className="text-gray-500 uppercase tracking-wider font-semibold">Total Provider:</span>
            <span className="font-bold text-gray-800 bg-gray-50 px-2 py-1 rounded border">
              {calculateProviderTotal(pData).total_jumlah} trx | Rp {calculateProviderTotal(pData).total_tagihan.toLocaleString('id-ID')}
            </span>
          </div>
        )}
      </div>
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw size={40} className="animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Memuat laporan shift...</p>
        </div>
      </div>
    );
  }

  if (createdLaporan) {
    const grandTotal = calculateGrandTotals();
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle size={64} className="mx-auto mb-4 text-green-600" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Laporan Shift Berhasil!</h2>
          <p className="text-gray-600 mb-2">Nomor: {createdLaporan.nomor_laporan}</p>
          <p className="text-sm text-gray-500 mb-6">Silakan upload bukti transaksi</p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold mb-3">Ringkasan:</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>Total Transaksi: <strong>{grandTotal.total_transaksi}</strong></div>
              <div>Total Tagihan: <strong>Rp {grandTotal.total_tagihan.toLocaleString('id-ID')}</strong></div>
              <div>TUNAI: <strong>{grandTotal.total_tunai_trx} trx</strong></div>
              <div>NON-TUNAI: <strong>{grandTotal.total_non_tunai_trx} trx</strong></div>
            </div>
          </div>

          {/* Upload Bukti Section */}
          <div className="mb-6 max-w-md mx-auto bg-gray-50 border border-gray-100 p-4 rounded-xl">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📎 Pilih Bukti Transaksi (Foto/PDF)
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setUploadFile(e.target.files[0])}
              className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
            />
            {uploadFile && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700 flex items-center justify-between">
                <span className="truncate font-medium">📄 {uploadFile.name}</span>
                <button
                  type="button"
                  onClick={() => setUploadFile(null)}
                  className="text-red-500 hover:text-red-700 font-bold ml-2"
                >
                  Hapus
                </button>
              </div>
            )}
            
            <button
              type="button"
              onClick={async () => {
                if (!uploadFile) return;
                setUploadingProof(true);
                try {
                  const token = localStorage.getItem('token');
                  const formData = new FormData();
                  formData.append('file', uploadFile);

                  const response = await fetch(`${api}/api/laporan-shift/${createdLaporan.id}/upload-bukti`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                  });

                  if (!response.ok) throw new Error('Gagal upload bukti');

                  setSuccess('Bukti berhasil disimpan!');
                  setUploadFile(null);
                  setTimeout(() => setSuccess(''), 3000);
                } catch (err) {
                  setError('Gagal menyimpan bukti: ' + err.message);
                } finally {
                  setUploadingProof(false);
                }
              }}
              disabled={uploadingProof || !uploadFile}
              className="w-full mt-3 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {uploadingProof ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Simpan Bukti
                </>
              )}
            </button>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Buat Laporan Lagi
            </button>
            <button
              onClick={() => window.location.href = '#/riwayat-loket'}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Lihat Riwayat
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bsiBalance = calculateBankEndingBalance('BSI');
  const mandiriBalance = calculateBankEndingBalance('MANDIRI');
  const grandTotal = calculateGrandTotals();

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Laporan Shift - Dual Bank</h1>
            <p className="text-gray-600">Input transaksi PPOB per bank dengan pemisahan TUNAI/NON-TUNAI</p>
          </div>

          {/* Edit Saldo Awal - Removed: Use Inject Saldo menu in Manager role instead */}
        </div>

        {/* Saldo Info */}
        <div className="mt-4 flex gap-4 text-sm">
          <div className="bg-gray-100 px-3 py-1 rounded">
            <span className="text-gray-600">Saldo BSI: </span>
            <span className="font-semibold text-gray-800">Rp {saldoAwal.bsi.toLocaleString('id-ID')}</span>
          </div>
          <div className="bg-gray-100 px-3 py-1 rounded">
            <span className="text-gray-600">Saldo Mandiri: </span>
            <span className="font-semibold text-gray-800">Rp {saldoAwal.mandiri.toLocaleString('id-ID')}</span>
          </div>
          {saldoAwal.loaded && saldoAwal.lastDate && (
            <span className="text-gray-500 italic">Dari: {saldoAwal.lastDate}</span>
          )}
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle size={20} className="text-green-500 mr-2" />
              <p className="text-green-700">{success}</p>
            </div>
            {createdLaporan && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="ml-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
              >
                <Upload size={18} />
                Upload Bukti Transaksi
              </button>
            )}
          </div>
        </div>
      )}


      <form onSubmit={handleSubmit}>
        {/* Shift Period Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shift</label>
              <select
                value={formData.shift_period}
                onChange={async (e) => {
                  const newShift = e.target.value;
                  setFormData({ ...formData, shift_period: newShift });
                  // Reload saldo awal saat shift berubah - agar dapat saldo akhir shift sebelumnya
                  await loadSaldoAwal();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {shiftSettings.length > 0 ? (
                  shiftSettings.map(shift => (
                    <option key={shift.id} value={shift.shift_name}>
                      {shift.shift_name} ({shift.jam_mulai} - {shift.jam_selesai})
                    </option>
                  ))
                ) : (
                  <option value="PAGI">Pagi (08:00 - 14:00)</option>
                )}
              </select>

              {/* Shift Time Inline Editor - Owner / Manager Only */}
              {(user?.role === 'OWNER' || user?.role === 'MANAGER') && activeShiftObj && (
                <div className="mt-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg transition-all duration-300">
                  <div className="text-xs font-semibold text-indigo-800 mb-2 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-indigo-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Edit Jam Shift: {formData.shift_period}
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Mulai"
                        value={editShiftTimes.jam_mulai}
                        onChange={(e) => setEditShiftTimes({ ...editShiftTimes, jam_mulai: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 bg-white font-medium text-gray-800"
                      />
                    </div>
                    <span className="text-xs text-indigo-400 font-bold">-</span>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Selesai"
                        value={editShiftTimes.jam_selesai}
                        onChange={(e) => setEditShiftTimes({ ...editShiftTimes, jam_selesai: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 bg-white font-medium text-gray-800"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveShiftTimes}
                      className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded shadow-sm transition-colors flex items-center gap-1"
                    >
                      <Save size={12} />
                      Simpan
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Saldo Awal BSI</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg h-[42px] flex items-center">
                {loadingSaldo ? 'Loading...' : `Rp ${saldoAwal.bsi.toLocaleString('id-ID')}`}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Saldo Awal Mandiri</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg h-[42px] flex items-center">
                {loadingSaldo ? 'Loading...' : `Rp ${saldoAwal.mandiri.toLocaleString('id-ID')}`}
              </div>
            </div>
          </div>
        </div>

        {/* Bank Tabs */}
        <div className="bg-white rounded-lg shadow mb-4">
          {/* Tab Headers */}
          <div className="flex border-b overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('BSI')}
              className={`flex-1 min-w-[120px] px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'BSI'
                  ? 'border-b-2 border-green-500 text-green-700 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2 font-semibold">
                <Wallet size={20} />
                BSI
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('MANDIRI')}
              className={`flex-1 min-w-[120px] px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'MANDIRI'
                  ? 'border-b-2 border-blue-500 text-blue-700 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2 font-semibold">
                <DollarSign size={20} />
                Bank Mandiri
              </div>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'BSI' && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-green-700 flex items-center gap-2">
                  <Wallet size={20} />
                  Transaksi BSI
                </h3>
                {standardProviders.map(provider => (
                  <ProviderTransactionInput
                    key={provider.id}
                    provider={provider}
                    bankId="BSI"
                  />
                ))}
                {standardProviders.length === 0 && (
                  <p className="text-gray-500 text-center py-8">Belum ada provider standar untuk BSI</p>
                )}
              </div>
            )}

            {activeTab === 'MANDIRI' && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-700 flex items-center gap-2">
                  <DollarSign size={20} />
                  Transaksi Bank Mandiri
                </h3>
                {standardProviders.map(provider => (
                  <ProviderTransactionInput
                    key={provider.id}
                    provider={provider}
                    bankId="MANDIRI"
                  />
                ))}
                {standardProviders.length === 0 && (
                  <p className="text-gray-500 text-center py-8">Belum ada provider standar untuk Bank Mandiri</p>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
            <h3 className="font-semibold mb-3">BSI</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Saldo Awal:</span>
                <span className="font-semibold">Rp {bsiBalance.saldo_awal.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Transaksi:</span>
                <span className="font-semibold">{bsiBalance.total_transaksi} trx</span>
              </div>
              <div className="flex justify-between">
                <span>Total Pengurangan:</span>
                <span className="font-semibold">Rp {bsiBalance.total_pengurangan.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between border-t border-white/30 pt-2">
                <span className="font-semibold">Saldo Akhir:</span>
                <span className="font-bold text-lg">Rp {bsiBalance.saldo_akhir.toLocaleString('id-ID')}</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
            <h3 className="font-semibold mb-3">Bank Mandiri</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Saldo Awal:</span>
                <span className="font-semibold">Rp {mandiriBalance.saldo_awal.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Transaksi:</span>
                <span className="font-semibold">{mandiriBalance.total_transaksi} trx</span>
              </div>
              <div className="flex justify-between">
                <span>Total Pengurangan:</span>
                <span className="font-semibold">Rp {mandiriBalance.total_pengurangan.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between border-t border-white/30 pt-2">
                <span className="font-semibold">Saldo Akhir:</span>
                <span className="font-bold text-lg">Rp {mandiriBalance.saldo_akhir.toLocaleString('id-ID')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Grand Total */}
        <div className="bg-gray-800 rounded-lg shadow p-6 mb-4 text-white">
          <h3 className="font-semibold mb-4">Grand Total (Kedua Bank)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm opacity-80">Total Transaksi</p>
              <p className="text-2xl font-bold">{grandTotal.total_transaksi}</p>
            </div>
            <div className="text-center">
              <p className="text-sm opacity-80">Total Tagihan</p>
              <p className="text-xl font-bold">Rp {grandTotal.total_tagihan.toLocaleString('id-ID')}</p>
            </div>
            <div className="text-center">
              <p className="text-sm opacity-80">Total Admin</p>
              <p className="text-xl font-bold">Rp {grandTotal.total_admin.toLocaleString('id-ID')}</p>
            </div>
            <div className="text-center">
              <p className="text-sm opacity-80">Total Omzet</p>
              <p className="text-2xl font-bold text-green-400">Rp {grandTotal.total_omzet.toLocaleString('id-ID')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-700">
            <div className="text-center">
              <p className="text-sm opacity-80">TUNAI: {grandTotal.total_tunai_trx} trx</p>
              <p className="text-lg font-bold text-green-400">Rp {grandTotal.total_tunai_amount.toLocaleString('id-ID')}</p>
              <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${(grandTotal.total_tunai_trx / grandTotal.total_transaksi * 100) || 0}%` }}
                />
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm opacity-80">NON-TUNAI: {grandTotal.total_non_tunai_trx} trx</p>
              <p className="text-lg font-bold text-blue-400">Rp {grandTotal.total_non_tunai_amount.toLocaleString('id-ID')}</p>
              <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${(grandTotal.total_non_tunai_trx / grandTotal.total_transaksi * 100) || 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History - Real-time */}
        {(grandTotal.total_transaksi > 0) && (
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-600" />
                Ringkasan Transaksi Per Provider
              </h3>
              <span className="text-sm text-gray-500">
                Total: {grandTotal.total_transaksi} transaksi
              </span>
            </div>

            <div className="space-y-3">
              {/* BSI Providers */}
              {Object.keys(bankTransaksi.BSI || {}).map(providerId => {
                const provider = providers.find(p => p.id === providerId);
                if (!provider) return null;

                const pData = bankTransaksi.BSI[providerId];
                const providerTotal = calculateProviderTotal(pData);

                if (providerTotal.total_jumlah === 0) return null;

                return (
                  <div key={`BSI-${providerId}`} className="border-l-4 border-green-500 bg-green-50 rounded-r-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">{provider.nama_provider}</span>
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">BSI</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                          {pData.tunai.checked && (
                            <div>
                              <span className="text-gray-600">TUNAI: </span>
                              <span className="font-semibold">{pData.tunai.jumlah || 0} trx</span>
                              <span className="text-gray-500 ml-1">Rp {(pData.tunai.tagihan || 0).toLocaleString('id-ID')}</span>
                            </div>
                          )}
                          {pData.non_tunai.checked && (
                            <div>
                              <span className="text-gray-600">NON-TUNAI: </span>
                              <span className="font-semibold">{pData.non_tunai.jumlah || 0} trx</span>
                              <span className="text-gray-500 ml-1">Rp {(pData.non_tunai.tagihan || 0).toLocaleString('id-ID')}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-600">Admin: </span>
                            <span className="font-semibold">Rp {providerTotal.total_admin.toLocaleString('id-ID')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Total Tagihan</div>
                        <div className="text-lg font-bold text-green-700">
                          Rp {providerTotal.total_tagihan.toLocaleString('id-ID')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* MANDIRI Providers */}
              {Object.keys(bankTransaksi.MANDIRI || {}).map(providerId => {
                const provider = providers.find(p => p.id === providerId);
                if (!provider) return null;

                const pData = bankTransaksi.MANDIRI[providerId];
                const providerTotal = calculateProviderTotal(pData);

                if (providerTotal.total_jumlah === 0) return null;

                return (
                  <div key={`MANDIRI-${providerId}`} className="border-l-4 border-blue-500 bg-blue-50 rounded-r-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">{provider.nama_provider}</span>
                          <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">Mandiri</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                          {pData.tunai.checked && (
                            <div>
                              <span className="text-gray-600">TUNAI: </span>
                              <span className="font-semibold">{pData.tunai.jumlah || 0} trx</span>
                              <span className="text-gray-500 ml-1">Rp {(pData.tunai.tagihan || 0).toLocaleString('id-ID')}</span>
                            </div>
                          )}
                          {pData.non_tunai.checked && (
                            <div>
                              <span className="text-gray-600">NON-TUNAI: </span>
                              <span className="font-semibold">{pData.non_tunai.jumlah || 0} trx</span>
                              <span className="text-gray-500 ml-1">Rp {(pData.non_tunai.tagihan || 0).toLocaleString('id-ID')}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-600">Admin: </span>
                            <span className="font-semibold">Rp {providerTotal.total_admin.toLocaleString('id-ID')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Total Tagihan</div>
                        <div className="text-lg font-bold text-blue-700">
                          Rp {providerTotal.total_tagihan.toLocaleString('id-ID')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {grandTotal.total_transaksi === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Info size={20} className="inline mr-2" />
                Belum ada transaksi yang diinput
              </div>
            )}
          </div>
        )}

        {/* Uang Kecil & Catatan */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Uang Kecil / Kembalian (Rp)
            </label>
            <input
              type="text"
              min="0"
              value={uangKecil}
              onChange={(e) => setUangKecil(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-2">
              <Info size={12} className="inline mr-1" />
              Gabungan untuk kedua bank
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catatan (Opsional)
            </label>
            <textarea
              value={formData.catatan}
              onChange={(e) => setFormData({ ...formData, catatan: e.target.value })}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Tambahkan catatan jika diperlukan..."
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-gray-50 rounded-lg p-4">
          <div className="flex flex-wrap gap-3">
            {/* Calculator Button */}
            <button
              type="button"
              onClick={() => setShowCalculator(true)}
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow"
            >
              <Calculator size={20} />
              Kalkulator
            </button>

            {/* Upload Proof Button */}
            {createdLaporan && (
              <button
                type="button"
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow"
              >
                <Upload size={20} />
                Upload Bukti
              </button>
            )}
          </div>

          {/* Submit Button - Selalu aktif */}
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-lg shadow-lg"
          >
            {submitting ? (
              <>
                <RefreshCw size={20} className="animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Save size={20} />
                Simpan Laporan Shift
              </>
            )}
          </button>
        </div>

        {/* Calculator Modal */}
        {showCalculator && (
          <SimpleCalculator
            onResult={handleCalculatorResult}
            onClose={() => setShowCalculator(false)}
          />
        )}

        {/* Upload Proof Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="bg-green-600 px-4 py-3 flex items-center justify-between rounded-t-xl">
                <h3 className="text-lg font-bold text-white">Upload Bukti Transaksi</h3>
                <button onClick={() => setShowUploadModal(false)} className="text-white hover:bg-white/20 p-1 rounded">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pilih File Bukti Transaksi
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {uploadFile && (
                    <p className="mt-2 text-sm text-gray-600">File: {uploadFile.name}</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleUploadProof}
                    disabled={uploadingProof || !uploadFile}
                    className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                  >
                    <Save size={16} />
                    {uploadingProof ? 'Menyimpan...' : 'Simpan Bukti'}
                  </button>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="flex-1 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
                  >
                    Batal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default LaporanShiftDualBankV2;
